import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { type SxProps, type Theme, Alert, Box, Button, FormHelperText, Typography } from '@mui/material';
import { Marketplaces } from 'common/constants/enums';
import { productsNumber } from 'common/constants/values';
import { enrichPageFormProductWithTemporaryLink } from 'common/utils';
import type { CommonPageForm } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { UseFieldArrayReturn, useFormContext, useWatch } from 'react-hook-form';
import type { PageFormProduct } from 'store/api/pages-api/pages-api-types';
import { productsApiSlice } from 'store/api/products-api/products-api';

import { ProductAdder } from '../product-adder';
import { ProductCard } from '../product-card';
import { styles } from './products-list.styles';

export type FieldProduct = CommonPageForm['products'][0];

interface Props {
  productFields: UseFieldArrayReturn<CommonPageForm, 'products', 'id'>;
  dialogOpen: boolean;
  onDialogClose: () => void;
  onAddClick: () => void;
  onProductsReordered?: () => void;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}

export const ProductsList = ({
  dialogOpen, onAddClick, onDialogClose, onProductsReordered, onSelect, productFields, selectedId,
}: Props) => {
  const {
    append,
    fields,
    move,
    remove,
    update,
  } = productFields;
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<CommonPageForm>();

  const publisher = useWatch({
    control,
    name: 'publisher',
  });
  const region = publisher?.country || 'us';

  const [fetchFullProductData] = productsApiSlice.useLazyGetFullProductDataQuery();

  const handleAddProduct = async (productId: string, productMarketplace: Marketplaces) => {
    const doubleProductIndex = fields.findIndex(
      (item) => String(item.product_id) === String(productId),
    );

    if (doubleProductIndex !== -1) {
      const doubleProduct = fields[doubleProductIndex];

      if (doubleProduct.status !== 'excluded') {
        throw new Error('Product already exists');
      }

      const visibleFieldsCount = fields.filter((f) => f.status !== 'excluded').length;
      const isFirst = visibleFieldsCount === 0;

      const restoredProduct: FieldProduct = {
        ...doubleProduct,
        status: doubleProduct.original_id ? undefined : 'new',
        order: isFirst ? 1 : visibleFieldsCount + 1,
        value: isFirst ? 'Best Pick' : '',
      };

      remove(doubleProductIndex);
      append(restoredProduct);

      return;
    }

    try {
      const {
        data,
        isError,
      } = await fetchFullProductData({
        id: productId,
        marketplace: productMarketplace,
        options: {
          ...(productMarketplace === Marketplaces.Amazon ? { features: true } :
            { include_product_features: true }),
          region
        },
      });

      if (isError || !data) throw new Error('Not found');

      const isFirst = fields.length === 0;

      const mappedContent: FieldProduct['content'] = {
        ...data.content,
        // @ts-ignore
        pros: data.content.pros?.map((proString) => ({ value: String(proString) })) || [],
      };

      let newProduct: FieldProduct = {
        ...data,
        _initialTitle: data.title,
        content: mappedContent,
        value: isFirst ? 'Best Pick' : '',
        order: isFirst ? 1 : fields.length + 1,
        status: 'new',
        product_marketplace: productMarketplace,
      };

      newProduct = await enrichPageFormProductWithTemporaryLink(
        newProduct,
        productMarketplace,
        publisher?.id,
        region,
      );

      append(newProduct);
    } catch (e: unknown) {
      if (e instanceof Error && e.message === 'Not found') throw e;
      const errorMessage = e instanceof Error ? e.message : String(e);
      throw new Error(`Server error: ${errorMessage}`);
    }
  };

  const reorderFields = (snapshot: typeof fields | PageFormProduct[]) => {
    let visibleIdx = 0;
    snapshot.forEach((item, idx) => {
      if (item.status === 'excluded') return;
      const newOrder = visibleIdx + 1;
      const newValue = visibleIdx === 0
        ? 'Best Pick'
        : item.value === 'Best Pick' ? '' : item.value;
      if (item.order !== newOrder) {
        setValue(`products.${idx}.order`, newOrder, { shouldDirty: true });
      }
      if (item.value !== newValue) {
        setValue(`products.${idx}.value`, newValue, { shouldDirty: true });
      }
      visibleIdx++;
    });
  };

  const handleRemoveProduct = (index: number) => {
    const freshProducts = getValues('products') || [];
    const product = freshProducts[index];

    if (!product) return;

    if (product.status === 'new') {
      remove(index);
      reorderFields(freshProducts.filter((_, i) => i !== index));
    } else {
      update(index, { ...product, status: 'excluded' });
      reorderFields(
        freshProducts.map((f, i) => (i === index ? { ...f, status: 'excluded' as const } : f)),
      );
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    if (startIndex === endIndex) return;

    const actualStartIndex = visibleFields[startIndex].originalIndex;
    const actualEndIndex = visibleFields[endIndex].originalIndex;
    move(actualStartIndex, actualEndIndex);
    onProductsReordered?.();

    const postMove = [...fields];
    const [moved] = postMove.splice(actualStartIndex, 1);
    postMove.splice(actualEndIndex, 0, moved);
    reorderFields(postMove);
  };

  const visibleFields = fields
    .slice(0, productsNumber)
    .map((item, originalIndex) => ({ ...item, originalIndex }))
    .filter((item) => item.status !== 'excluded');

  const productsError = errors.products?.message || errors.products?.root?.message;

  if (!enabled) return null;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      <ProductAdder
        currentProductsCount={fields.length}
        onAddProduct={handleAddProduct}
        onClose={onDialogClose}
        open={dialogOpen}
      />

      {visibleFields.length > 0 && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: '#EEF2FF',
            borderColor: '#C7D2FE',
            color: '#3730A3',
            '& .MuiAlert-icon': { color: '#6366F1' },
          }}
        >
          The first product will always be <strong>&quot;Best Pick&quot;</strong>
        </Alert>
      )}

      {productsError && (
        <FormHelperText
          error
          sx={{
            fontSize: '14px',
            ml: 0,
          }}>
          {productsError}
        </FormHelperText>
      )}

      {visibleFields.length === 0 ? (
        <Box sx={styles.emptyState}>
          <Inventory2OutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={500} variant="body1">
            No products added yet
          </Typography>
          <Button
            onClick={onAddClick}
            size="large"
            startIcon={<AddCircleOutlineIcon />}
            sx={{ mt: 1 }}
            variant="outlined"
          >
            Add Product
          </Button>
        </Box>
      ) : (
        <Box sx={styles.listWrapper}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable direction="horizontal" droppableId="products-droppable">
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  sx={
                    [
                      styles.slider,
                      snapshot.isDraggingOver && styles.sliderDraggingOver,
                    ] as SxProps<Theme>
                  }
                  {...provided.droppableProps}
                >
                  {visibleFields.map((item, index) => (
                    <Draggable
                      key={item.product_id}
                      draggableId={String(item.product_id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}
                          sx={
                            [
                              styles.itemContainer,
                              snapshot.isDragging && styles.itemDragging,
                            ] as SxProps<Theme>
                          }
                        >
                          <ProductCard
                            index={item.originalIndex}
                            isSelected={selectedId === String(item.product_id)}
                            item={item}
                            onDelete={handleRemoveProduct}
                            onSelect={() => onSelect?.(String(item.product_id))}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}
    </Box>
  );
};
